import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminFetchBtwLabels,
  adminCreateBtwLabel,
  adminUpdateBtwLabel,
  adminArchiveBtwLabel,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";
import BtwLabelPill, { LABEL_COLOR_OPTIONS, getLabelColors } from "../../components/btw-label-pill";

const emptyForm = { name: "", color: "slate" };

const BtwLabelsPage = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetchBtwLabels({ includeArchived: showArchived });
      setLabels(res?.data ?? res ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showArchived]);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
    setError(null);
  };

  const startEdit = (label) => {
    setEditing(label._id);
    setForm({
      name: label.name,
      color: label.color,
    });
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Naam is verplicht.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing === "new") {
        await adminCreateBtwLabel(form);
      } else {
        await adminUpdateBtwLabel(editing, form);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setSaving(true);
    try {
      await adminArchiveBtwLabel(archiveTarget._id, !archiveTarget.archived);
      setArchiveTarget(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error ?? "Mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <span>BTW-teruggave</span>
            <span className="mx-1">/</span>
            <span>Labels</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">
            Labels
          </h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Tags die je orthogonaal aan workflow-status op een aanvraag kunt
            zetten. Eén aanvraag kan meerdere labels tegelijk hebben.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex h-8 cursor-pointer items-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={startNew}
            disabled={editing !== null}
            className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Nieuw label
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing !== null ? (
        <div className="mb-6 rounded bg-white p-5 shadow-card">
          <h3 className="mb-3 text-base font-semibold text-ink">
            {editing === "new" ? "Nieuw label" : "Label bewerken"}
          </h3>
          <div className="space-y-4">
            <label className="block text-sm">
              <span className="text-xs font-semibold text-ink-subtlest">Naam</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bijv. Wacht op klant"
                className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                disabled={saving}
              />
            </label>

            <div>
              <span className="block text-xs font-semibold text-ink-subtlest">Kleur</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {LABEL_COLOR_OPTIONS.map((c) => {
                  const colors = getLabelColors(c);
                  const isSelected = form.color === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      disabled={saving}
                      className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text} ${
                        isSelected ? "ring-2 ring-brand" : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                      {c}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3">
                <span className="text-xs text-ink-subtlest">Preview:</span>{" "}
                <BtwLabelPill label={{ name: form.name || "Label", color: form.color }} />
              </div>
            </div>

            {error ? (
              <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Lijst */}
      <div className="overflow-hidden rounded bg-white shadow-card">
        {loading ? (
          <div className="p-10 text-center text-sm text-ink-subtlest">Laden…</div>
        ) : labels.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-subtlest">
            Geen labels. Maak er een aan.
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {labels.map((label) => (
              <li key={label._id} className="flex items-center gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <BtwLabelPill label={label} />
                    {label.archived ? (
                      <span className="inline-flex items-center rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                        Gearchiveerd
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(label)}
                    disabled={editing !== null}
                    className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Bewerken
                  </button>
                  <button
                    type="button"
                    onClick={() => setArchiveTarget(label)}
                    disabled={editing !== null}
                    className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {label.archived ? "Terughalen" : "Archiveren"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={!!archiveTarget}
        title={archiveTarget?.archived ? "Terughalen?" : "Archiveren?"}
        message={
          archiveTarget?.archived
            ? `Het label "${archiveTarget?.name}" wordt weer beschikbaar.`
            : `Het label "${archiveTarget?.name}" wordt gearchiveerd en losgekoppeld van alle aanvragen waar het op staat.`
        }
        confirmLabel={archiveTarget?.archived ? "Terughalen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={saving}
        onConfirm={doArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default BtwLabelsPage;
